import React, { useMemo } from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Divider,
  Grid,
  Tooltip,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useAccessibleUserAccounts } from "../../../components/Common/HelperComponents/CustomHooks";
import { useOrganizations } from "../../../components/Common/HelperComponents/GetDataFromStore";
import { useCostInsightData } from "../CostInsight/useCostInsightData"; // Custom hook for fetching data
import ApiLoading from "../components/ApiLoading";
import ApiError from "../components/ApiError";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { NextPlanOutlined } from "@mui/icons-material";
import CloudProviderMark from "../../../components/Common/CloudProviderMark";

// Utility function to format dollar amounts
const formatDollarAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") {
    return "$0.00";
  }
  
  // Convert to number if it's a string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Check if it's a valid number
  if (isNaN(numAmount)) {
    return "$0.00";
  }
  
  // Format with commas and two decimal places
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

const CostOptimization = () => {
  const theme = useTheme();
  const history = useHistory();
  const accessibleAccounts = useAccessibleUserAccounts();
  const organizations = useOrganizations();

  const accCodes = useMemo(
    () => accessibleAccounts.map((account) => account.accountCode),
    [accessibleAccounts]
  );
  const orgCode = useMemo(() => organizations?.[0]?.orgcode, [organizations]);

  const { data, loading, error } = useCostInsightData(accCodes, orgCode);

  const dollarColor = "#228B22"; // US dollar green

  const handleGoTo = () => {
    console.log("Go-to action clicked");
    history.push("/costoptimization");
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        borderRadius: "8px",
        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        padding: "8px 12px",
        height: "100%", // Let height adjust automatically
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header with Icons */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mb: 1,
          paddingBottom: "2px",
          borderBottom: `1px solid`,
        }}
      >
        <Box display="flex" alignItems="center" minWidth={0}>
          <CloudProviderMark />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "text.primary" }}
          >
            Cost Optimization
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Tooltip title="Open Detailed View">
            <IconButton
              aria-label="go-to"
              sx={{
                color: "text.secondary",
                fontSize: "20px",
                transform: "rotate(-45deg)",
              }}
              onClick={handleGoTo}
            >
              <NextPlanOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ alignSelf: "center", height: "18px" }}
          />
          <IconButton
            aria-label="settings"
            className="drag-handle"
            sx={{ color: "text.secondary", fontSize: "20px" }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content Layout */}
      {loading ? (
        <ApiLoading parameter="Cost Insight" />
      ) : error ? (
        <ApiError parameter="Cost Insight" />
      ) : (
        <Grid container spacing={2}>
          {/* First Row */}
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Monthly Savings Opportunity
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: dollarColor, fontWeight: "bold" }}
            >
              {formatDollarAmount(data?.costInsightMonthlySavingsCost)}
            </Typography>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default CostOptimization;
